import { useState, useEffect, useRef } from "react";
import _ from "lodash";
import { GOOGLE, LINKEDIN, META, TIKTOK } from "../../../utils/constants";
import {
  startDeleteAdsContentUploaded,
  startListenerAdsContentsUploaded,
  startUpdateImageUrlAssetGroup,
  startUpdateMediaUrlAd,
  startUpdateMediaUrlAssetGroup,
  startUpdateImageUrlPlatformData,
  startUploadContentsToPlatforms,
} from "../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../lib/redux";
import { off } from "firebase/database";
import { useDebounced } from "../../../hooks/useDebounce";

const PLATFORMS_WITH_CACHED_CONTENT = [TIKTOK, META, LINKEDIN];

export function useUploadAsyncContentInCampaign({
  campaignID,
  setValue,
  getValues,
  watch,
}) {
  const mediaUrlsByPlatformRef = useRef({});
  const [adsContentsUploadedListener, setAdsContentsUploadedListener] =
    useState(null);
  const [dataReceived, setDataReceived] = useState({});

  const platforms = watch("platforms");
  const dispatch = useDispatchApp();

  // Function to send accumulated media URLs to endpoint and reset state
  const sendMediaUrlsToEndpoint = async () => {
    if (_.isEmpty(mediaUrlsByPlatformRef.current)) return;

    dispatch(
      startUploadContentsToPlatforms({
        mediaUrlsByPlatform: mediaUrlsByPlatformRef.current,
        campaignID,
      })
    );

    mediaUrlsByPlatformRef.current = {};
  };

  // Debounced function that will be called 5 seconds after the last call
  const debouncedSendMediaUrls = useDebounced(sendMediaUrlsToEndpoint, 5000);

  const onUpdatedAdsContentsUploaded = (data = {}) => {
    if (!data) return;
    if (typeof data === "string") return;

    setDataReceived((prevData) => ({ ...prevData, ...data }));

    const platforms = getValues("platforms");
    if (_.isEmpty(platforms)) {
      return;
    }

    data = {
      ...data,
      ...dataReceived,
    };

    const hasGooglePlatform = platforms.includes("google");

    const dataKeys = Object.keys(data || {});
    const dataToSearch = {};

    dataKeys.forEach((key) => {
      const content = data[key];
      dataToSearch[content.preview] = { ...content, id: key };
    });

    const adsGroups = getValues("adsGroups") || [];
    const assetsGroups = getValues("assetsGroups") || [];

    const newMediaUrls = {};

    adsGroups.forEach((adGroup, indexAdGroup) => {
      const platform = adGroup.platform;
      if (_.isEmpty(adGroup.ads)) return;

      adGroup.ads.forEach((ad, indexAd) => {
        if (_.isEmpty(ad.mediaUrls)) return;
        ad.mediaUrls.forEach((mediaUrl, indexMediaUrl) => {
          const content = dataToSearch[mediaUrl];
          if (!content?.value) {
            return;
          }

          if (PLATFORMS_WITH_CACHED_CONTENT.includes(platform)) {
            if (!newMediaUrls[platform]) {
              newMediaUrls[platform] = [];
            }
            newMediaUrls[platform].push(content.value);
          }

          onUpdateMediaUrlAndRemovePreviewAd({
            indexAdGroup,
            indexAd,
            indexMediaUrl,
            mediaUrl: content.value,
            contentID: content.id,
          });
        });
      });
    });

    assetsGroups.forEach((assetsGroup, indexAssetsGroup) => {
      (assetsGroup.imageUrls || []).forEach((imageUrl, indexImageUrl) => {
        const content = dataToSearch[imageUrl];
        if (!content?.value) {
          return;
        }

        onUpdateImageUrlAndRemovePreviewAssetGroup({
          indexAssetsGroup,
          indexImageUrl,
          imageUrl: content.value,
          contentID: content.id,
        });
      });

      if (assetsGroup.platform === LINKEDIN) {
        (assetsGroup.mediaUrls || []).forEach((mediaUrl, indexMediaUrl) => {
          const url = typeof mediaUrl === "string" ? mediaUrl : mediaUrl?.url;
          if (!url) return;

          const content = dataToSearch[url];
          if (!content?.value) {
            return;
          }

          if (PLATFORMS_WITH_CACHED_CONTENT.includes(LINKEDIN)) {
            if (!newMediaUrls[LINKEDIN]) {
              newMediaUrls[LINKEDIN] = [];
            }
            newMediaUrls[LINKEDIN].push(content.value);
          }

          onUpdateMediaUrlAndRemovePreviewAssetGroup({
            indexAssetsGroup,
            indexMediaUrl,
            mediaUrl: content.value,
            contentID: content.id,
          });
        });
      }
    });

    if (!_.isEmpty(newMediaUrls)) {
      // Merge current ref value with newMediaUrls, must be sum of current and newMediaUrls, for each platform
      const currentMediaUrls = mediaUrlsByPlatformRef.current;
      const newMediaUrlsByPlatform = { ...currentMediaUrls };
      Object.keys(newMediaUrls).forEach((platform) => {
        newMediaUrlsByPlatform[platform] = _.uniq([
          ...(currentMediaUrls[platform] || []),
          ...newMediaUrls[platform],
        ]);
      });
      mediaUrlsByPlatformRef.current = newMediaUrlsByPlatform;

      // Call debounced function to send to endpoint after 5 seconds of inactivity
      debouncedSendMediaUrls();
    }

    if (hasGooglePlatform) {
      const platformData = getValues(`platformsData.${GOOGLE}`);
      if (platformData?.imageUrls) {
        platformData.imageUrls.forEach((imageUrl, indexImageUrl) => {
          const content = dataToSearch[imageUrl];
          if (!content?.value) return;

          onUpdateImageUrlAndRemovePreviewInPlatformData({
            platform: GOOGLE,
            indexImageUrl,
            imageUrl: content.value,
            contentID: content.id,
          });
        });
      }
    }
  };

  const onUpdateImageUrlAndRemovePreviewInPlatformData = async ({
    platform,
    indexImageUrl,
    imageUrl,
    contentID,
  }) => {
    const responseUpdateImageUrl = await dispatch(
      startUpdateImageUrlPlatformData({
        campaignID,
        platform,
        imageUrl,
        index: indexImageUrl,
      })
    );
    if (!responseUpdateImageUrl) return;

    const responseDeleteAdsContentsUploaded = await dispatch(
      startDeleteAdsContentUploaded({
        campaignID,
        contentID,
      })
    );
    if (!responseDeleteAdsContentsUploaded) return;

    setValue(
      `platformsData.${platform}.imageUrls.[${indexImageUrl}]`,
      imageUrl,
      { shouldValidate: true, shouldDirty: true }
    );
  };
  const onUpdateImageUrlAndRemovePreviewAssetGroup = async ({
    indexAssetsGroup,
    indexImageUrl,
    imageUrl,
    contentID,
  }) => {
    const assetsGroup = getValues(`assetsGroups.[${indexAssetsGroup}]`);
    const assetsGroupID = assetsGroup.id;

    const responseUpdateImageUrl = await dispatch(
      startUpdateImageUrlAssetGroup({
        assetsGroupID,
        index: indexImageUrl,
        imageUrl,
      })
    );
    if (!responseUpdateImageUrl) return;

    const responseDeleteAdsContentsUploaded = await dispatch(
      startDeleteAdsContentUploaded({
        campaignID,
        contentID,
      })
    );
    if (!responseDeleteAdsContentsUploaded) return;

    setValue(
      `assetsGroups.[${indexAssetsGroup}].imageUrls.[${indexImageUrl}]`,
      imageUrl,
      { shouldValidate: true, shouldDirty: true }
    );
  };
  const onUpdateMediaUrlAndRemovePreviewAssetGroup = async ({
    indexAssetsGroup,
    indexMediaUrl,
    mediaUrl,
    contentID,
  }) => {
    const assetsGroup = getValues(`assetsGroups.[${indexAssetsGroup}]`);
    const assetsGroupID = assetsGroup.id;

    const responseUpdateMediaUrl = await dispatch(
      startUpdateMediaUrlAssetGroup({
        assetsGroupID,
        index: indexMediaUrl,
        mediaUrl,
      })
    );
    if (!responseUpdateMediaUrl) return;

    const responseDeleteAdsContentsUploaded = await dispatch(
      startDeleteAdsContentUploaded({
        campaignID,
        contentID,
      })
    );
    if (!responseDeleteAdsContentsUploaded) return;

    setValue(
      `assetsGroups.[${indexAssetsGroup}].mediaUrls.[${indexMediaUrl}]`,
      mediaUrl,
      { shouldValidate: true, shouldDirty: true }
    );
  };
  const onUpdateMediaUrlAndRemovePreviewAd = async ({
    indexAdGroup,
    indexAd,
    indexMediaUrl,
    mediaUrl,
    contentID,
  }) => {
    const ad = getValues(`adsGroups.[${indexAdGroup}].ads.[${indexAd}]`);
    const adID = ad.id;

    const responseUpdateMediaUrl = await dispatch(
      startUpdateMediaUrlAd({
        adID,
        mediaUrl,
        index: indexMediaUrl,
      })
    );
    if (!responseUpdateMediaUrl) return;

    const responseDeleteAdsContentsUploaded = await dispatch(
      startDeleteAdsContentUploaded({
        campaignID,
        contentID,
      })
    );
    if (!responseDeleteAdsContentsUploaded) return;

    setValue(
      `adsGroups.[${indexAdGroup}].ads.[${indexAd}].mediaUrls.[${indexMediaUrl}]`,
      mediaUrl,
      { shouldValidate: true, shouldDirty: true }
    );
  };

  useEffect(() => {
    if (!campaignID) return;

    dispatch(
      startListenerAdsContentsUploaded({
        campaignID,
        onReferenceAvailable: (listener) =>
          setAdsContentsUploadedListener(listener),
        onUpdatedDataCallback: (contents) =>
          onUpdatedAdsContentsUploaded(contents),
      })
    );

    return () => {
      if (adsContentsUploadedListener) {
        off(adsContentsUploadedListener);
      }
    };
    // eslint-disable-next-line
  }, [campaignID]);

  useEffect(() => {
    if (!campaignID) return;
    onUpdatedAdsContentsUploaded(dataReceived);
    // eslint-disable-next-line
  }, [platforms]);
}
